/**
 * Pool Shape Data — defines geometry anchors for the Live Layout Preview.
 * Each shape has a body path + anchor regions that features attach to.
 * ViewBox: 260x190. Pool body occupies roughly 35-225 x 30-155.
 */

// Pool body SVG paths (main water area outline)
export const POOL_PLAN_PATHS = {
  rectangle: 'M 40 38 L 220 38 L 220 148 L 40 148 Z',
  auto_cover_rectangle: 'M 40 38 L 220 38 L 220 148 L 40 148 Z',
  roman: 'M 40 38 L 220 38 L 220 108 A 90 40 0 0 1 40 108 Z',
  freeform: 'M 75 44 C 120 30, 180 36, 212 58 C 232 78, 222 115, 195 140 C 165 156, 105 152, 68 135 C 38 118, 42 72, 75 44 Z',
  grecian: 'M 68 38 L 192 38 L 222 60 L 222 126 L 192 148 L 68 148 L 38 126 L 38 60 Z',
  l_shape: 'M 40 38 L 145 38 L 145 85 L 220 85 L 220 148 L 40 148 Z',
  lazy_l: 'M 40 38 L 135 38 L 135 72 L 220 72 L 220 148 L 40 148 Z',
  kidney: 'M 95 40 C 150 28, 205 42, 218 80 C 228 115, 200 145, 155 148 C 110 150, 70 140, 55 118 C 42 98, 52 78, 72 72 C 88 67, 88 52, 95 40 Z',
  geometric_custom: 'M 40 38 L 220 38 L 220 148 L 40 148 Z',
  compact_plunge: 'M 60 44 L 200 44 L 200 142 L 60 142 Z',
  negative_edge: 'M 40 38 L 220 38 L 220 148 L 40 148 Z',
};

// Per-shape anchor data — features position relative to these
export const POOL_SHAPE_DATA = {
  rectangle: {
    shallowEdge: { x1: 40, y1: 38, x2: 220, y2: 38 },
    deepEdge: { x1: 40, y1: 148, x2: 220, y2: 148 },
    leftWall: { x1: 40, y1: 38, x2: 40, y2: 148 },
    rightWall: { x1: 220, y1: 38, x2: 220, y2: 148 },
    poolCenter: { x: 130, y: 93 },
    spaAttach: { left: {x: 22, y: 93}, right: {x: 238, y: 93}, center: {x: 130, y: 18} },
    slidePos: { left: {px: 18, py: 125, sx: 48, sy: 125}, right: {px: 242, py: 125, sx: 212, sy: 125}, deep_end: {px: 130, py: 163, sx: 130, sy: 142}, custom: {px: 242, py: 110, sx: 212, sy: 115} },
    waterWall: { x1: 55, y1: 148, x2: 205, y2: 148 },
    ledgeZone: { x: 40, y: 38, w: 180, maxH: 32 },
    benchLeft: { x: 40, y: 55, w: 15, h: 78 },
    benchDeep: { x: 55, y: 133, w: 150, h: 15 },
    benchRight: { x: 205, y: 55, w: 15, h: 78 },
    coverHousing: { x: 40, y: 24, w: 180, h: 12 },
    coverTrackL: { x: 58, y1: 38, y2: 148 },
    coverTrackR: { x: 202, y1: 38, y2: 148 },
    fireBowls: [{ x: 22, y: 160 }, { x: 238, y: 160 }],
    firePit: { x: 238, y: 22 },
  },
  auto_cover_rectangle: null, // uses rectangle + cover
  roman: {
    shallowEdge: { x1: 80, y1: 38, x2: 180, y2: 38 },
    deepEdge: { x1: 80, y1: 148, x2: 180, y2: 148 },
    leftWall: { x1: 48, y1: 60, x2: 48, y2: 126 },
    rightWall: { x1: 212, y1: 60, x2: 212, y2: 126 },
    poolCenter: { x: 130, y: 93 },
    spaAttach: { left: {x: 25, y: 93}, right: {x: 235, y: 93}, center: {x: 130, y: 18} },
    slidePos: { left: {px: 18, py: 120, sx: 52, sy: 118}, right: {px: 242, py: 120, sx: 208, sy: 118}, deep_end: {px: 130, py: 163, sx: 130, sy: 142}, custom: {px: 235, py: 105, sx: 208, sy: 108} },
    waterWall: { x1: 80, y1: 148, x2: 180, y2: 148 },
    ledgeZone: { x: 80, y: 38, w: 100, maxH: 30 },
    benchLeft: { x: 50, y: 62, w: 14, h: 62 },
    benchDeep: { x: 80, y: 133, w: 100, h: 14 },
    benchRight: { x: 196, y: 62, w: 14, h: 62 },
    coverHousing: { x: 80, y: 24, w: 100, h: 12 },
    coverTrackL: { x: 68, y1: 50, y2: 138 },
    coverTrackR: { x: 192, y1: 50, y2: 138 },
    fireBowls: [{ x: 25, y: 158 }, { x: 235, y: 158 }],
    firePit: { x: 235, y: 22 },
  },
  freeform: {
    shallowEdge: { x1: 70, y1: 42, x2: 195, y2: 42 },
    deepEdge: { x1: 62, y1: 138, x2: 195, y2: 138 },
    leftWall: { x1: 42, y1: 70, x2: 55, y2: 132 },
    rightWall: { x1: 218, y1: 65, x2: 200, y2: 130 },
    poolCenter: { x: 132, y: 95 },
    spaAttach: { left: {x: 22, y: 90}, right: {x: 238, y: 80}, center: {x: 135, y: 15} },
    slidePos: { left: {px: 15, py: 118, sx: 52, sy: 120}, right: {px: 245, py: 110, sx: 208, sy: 115}, deep_end: {px: 128, py: 165, sx: 128, sy: 140}, custom: {px: 238, py: 125, sx: 205, sy: 125} },
    waterWall: { x1: 70, y1: 142, x2: 185, y2: 145 },
    ledgeZone: { x: 78, y: 40, w: 110, maxH: 30 },
    benchLeft: { x: 44, y: 72, w: 14, h: 58 },
    benchDeep: { x: 70, y: 128, w: 118, h: 14 },
    benchRight: { x: 202, y: 68, w: 14, h: 58 },
    coverHousing: null, // freeform typically doesn't have auto-cover
    coverTrackL: null,
    coverTrackR: null,
    fireBowls: [{ x: 22, y: 158 }, { x: 240, y: 155 }],
    firePit: { x: 240, y: 22 },
  },
  kidney: {
    shallowEdge: { x1: 80, y1: 40, x2: 195, y2: 42 },
    deepEdge: { x1: 95, y1: 145, x2: 175, y2: 150 },
    leftWall: { x1: 72, y1: 50, x2: 85, y2: 135 },
    rightWall: { x1: 208, y1: 55, x2: 175, y2: 140 },
    poolCenter: { x: 140, y: 90 },
    spaAttach: { left: {x: 35, y: 85}, right: {x: 235, y: 80}, center: {x: 140, y: 15} },
    slidePos: { left: {px: 28, py: 120, sx: 70, sy: 118}, right: {px: 240, py: 108, sx: 200, sy: 112}, deep_end: {px: 135, py: 165, sx: 135, sy: 142}, custom: {px: 235, py: 118, sx: 200, sy: 120} },
    waterWall: { x1: 100, y1: 148, x2: 170, y2: 150 },
    ledgeZone: { x: 90, y: 40, w: 100, maxH: 28 },
    benchLeft: { x: 74, y: 58, w: 13, h: 55 },
    benchDeep: { x: 100, y: 132, w: 70, h: 13 },
    benchRight: { x: 192, y: 60, w: 13, h: 55 },
    coverHousing: null,
    coverTrackL: null,
    coverTrackR: null,
    fireBowls: [{ x: 25, y: 160 }, { x: 240, y: 158 }],
    firePit: { x: 240, y: 22 },
  },
  grecian: {
    shallowEdge: { x1: 68, y1: 38, x2: 192, y2: 38 },
    deepEdge: { x1: 68, y1: 148, x2: 192, y2: 148 },
    leftWall: { x1: 38, y1: 60, x2: 38, y2: 126 },
    rightWall: { x1: 222, y1: 60, x2: 222, y2: 126 },
    poolCenter: { x: 130, y: 93 },
    spaAttach: { left: {x: 18, y: 93}, right: {x: 242, y: 93}, center: {x: 130, y: 18} },
    slidePos: { left: {px: 12, py: 115, sx: 42, sy: 115}, right: {px: 248, py: 115, sx: 218, sy: 115}, deep_end: {px: 130, py: 163, sx: 130, sy: 142}, custom: {px: 245, py: 105, sx: 218, sy: 108} },
    waterWall: { x1: 68, y1: 148, x2: 192, y2: 148 },
    ledgeZone: { x: 68, y: 38, w: 124, maxH: 30 },
    benchLeft: { x: 40, y: 62, w: 14, h: 62 },
    benchDeep: { x: 68, y: 133, w: 124, h: 14 },
    benchRight: { x: 206, y: 62, w: 14, h: 62 },
    coverHousing: { x: 68, y: 24, w: 124, h: 12 },
    coverTrackL: { x: 50, y1: 48, y2: 138 },
    coverTrackR: { x: 210, y1: 48, y2: 138 },
    fireBowls: [{ x: 18, y: 160 }, { x: 242, y: 160 }],
    firePit: { x: 242, y: 22 },
  },
  l_shape: {
    shallowEdge: { x1: 40, y1: 38, x2: 145, y2: 38 },
    deepEdge: { x1: 40, y1: 148, x2: 220, y2: 148 },
    leftWall: { x1: 40, y1: 38, x2: 40, y2: 148 },
    rightWall: { x1: 220, y1: 85, x2: 220, y2: 148 },
    poolCenter: { x: 130, y: 100 },
    spaAttach: { left: {x: 20, y: 93}, right: {x: 240, y: 118}, center: {x: 92, y: 18} },
    slidePos: { left: {px: 18, py: 130, sx: 48, sy: 130}, right: {px: 242, py: 130, sx: 212, sy: 130}, deep_end: {px: 130, py: 163, sx: 130, sy: 142}, custom: {px: 242, py: 118, sx: 212, sy: 118} },
    waterWall: { x1: 55, y1: 148, x2: 205, y2: 148 },
    ledgeZone: { x: 40, y: 38, w: 105, maxH: 28 },
    benchLeft: { x: 40, y: 55, w: 14, h: 78 },
    benchDeep: { x: 55, y: 133, w: 150, h: 14 },
    benchRight: { x: 206, y: 95, w: 14, h: 42 },
    coverHousing: null,
    coverTrackL: null,
    coverTrackR: null,
    fireBowls: [{ x: 20, y: 160 }, { x: 242, y: 160 }],
    firePit: { x: 242, y: 22 },
  },
  lazy_l: null, // fallback to l_shape data
  compact_plunge: {
    shallowEdge: { x1: 60, y1: 44, x2: 200, y2: 44 },
    deepEdge: { x1: 60, y1: 142, x2: 200, y2: 142 },
    leftWall: { x1: 60, y1: 44, x2: 60, y2: 142 },
    rightWall: { x1: 200, y1: 44, x2: 200, y2: 142 },
    poolCenter: { x: 130, y: 93 },
    spaAttach: { left: {x: 38, y: 93}, right: {x: 222, y: 93}, center: {x: 130, y: 24} },
    slidePos: { left: {px: 30, py: 115, sx: 66, sy: 115}, right: {px: 225, py: 115, sx: 194, sy: 115}, deep_end: {px: 130, py: 158, sx: 130, sy: 138}, custom: {px: 222, py: 108, sx: 194, sy: 110} },
    waterWall: { x1: 72, y1: 142, x2: 188, y2: 142 },
    ledgeZone: { x: 60, y: 44, w: 140, maxH: 26 },
    benchLeft: { x: 60, y: 58, w: 13, h: 68 },
    benchDeep: { x: 72, y: 128, w: 116, h: 13 },
    benchRight: { x: 187, y: 58, w: 13, h: 68 },
    coverHousing: { x: 60, y: 30, w: 140, h: 12 },
    coverTrackL: { x: 74, y1: 44, y2: 142 },
    coverTrackR: { x: 186, y1: 44, y2: 142 },
    fireBowls: [{ x: 28, y: 155 }, { x: 232, y: 155 }],
    firePit: { x: 232, y: 24 },
  },
  negative_edge: null, // uses rectangle data
  geometric_custom: null, // uses grecian as close approximation
};

// Resolve shape data with fallback chain
export function getShapeData(shapeKey, length, width) {
  // If dimensions provided, return parametric anchors
  if (length && width) {
    return generateShapeAnchors(shapeKey, length, width);
  }
  // Legacy fixed data
  if (POOL_SHAPE_DATA[shapeKey]) return POOL_SHAPE_DATA[shapeKey];
  if (shapeKey === 'auto_cover_rectangle' || shapeKey === 'negative_edge') return POOL_SHAPE_DATA.rectangle;
  if (shapeKey === 'lazy_l') return POOL_SHAPE_DATA.l_shape;
  if (shapeKey === 'geometric_custom') return POOL_SHAPE_DATA.grecian;
  return POOL_SHAPE_DATA.rectangle;
}

// ============================================================
// PARAMETRIC GENERATORS — Scale system: 1 ft = 16px
// Canvas: (length*16)+96 wide, (width*16)+96 tall
// Pool origin: (48, 48), Pool size: length*16 × width*16
// ============================================================

const SCALE = 16; // px per foot
const MARGIN = 48; // px each side

/**
 * Compute parametric layout dimensions from pool feet.
 * Exported for use by PoolDesignEditor.
 */
export function computeLayout(lengthFt, widthFt) {
  // Fallback to 32×16 if empty/zero
  const l = (lengthFt && Number(lengthFt) > 0) ? Number(lengthFt) : 32;
  const w = (widthFt && Number(widthFt) > 0) ? Number(widthFt) : 16;

  let poolW = l * SCALE;
  let poolH = w * SCALE;
  let viewW = poolW + MARGIN * 2;
  let viewH = poolH + MARGIN * 2;

  // Enforce max canvas 960×640 — scale down proportionally
  const maxW = 960, maxH = 640;
  if (viewW > maxW || viewH > maxH) {
    const scaleFactor = Math.min(maxW / viewW, maxH / viewH);
    poolW = Math.round(poolW * scaleFactor);
    poolH = Math.round(poolH * scaleFactor);
    viewW = poolW + MARGIN * 2;
    viewH = poolH + MARGIN * 2;
  }

  // Enforce minimum 400×300
  viewW = Math.max(viewW, 400);
  viewH = Math.max(viewH, 300);

  const ox = MARGIN;
  const oy = MARGIN;
  const effectiveScale = poolW / l; // actual px/ft after clamping

  return { viewW, viewH, ox, oy, poolW, poolH, lengthFt: l, widthFt: w, scale: effectiveScale };
}

/**
 * Generate parametric SVG path for pool body.
 */
export function generatePoolPath(shape, poolW, poolH, ox, oy) {
  const rx = ox + poolW;
  const by = oy + poolH;
  const p = v => Math.round(v * 10) / 10;

  // Simple rectangle shapes
  if (['rectangle', 'auto_cover_rectangle', 'geometric_custom', 'compact_plunge', 'negative_edge'].includes(shape)) {
    return `M ${ox} ${oy} L ${rx} ${oy} L ${rx} ${by} L ${ox} ${by} Z`;
  }

  if (shape === 'roman') {
    const splitY = p(oy + poolH * 0.636);
    const arcRx = p(poolW / 2);
    const arcRy = p(poolH * 0.364);
    return `M ${ox} ${oy} L ${rx} ${oy} L ${rx} ${splitY} A ${arcRx} ${arcRy} 0 0 1 ${ox} ${splitY} Z`;
  }

  if (shape === 'freeform') {
    return `M ${p(ox+poolW*0.194)} ${p(oy+poolH*0.055)} ` +
      `C ${p(ox+poolW*0.444)} ${p(oy-poolH*0.073)}, ${p(ox+poolW*0.778)} ${p(oy-poolH*0.018)}, ${p(ox+poolW*0.956)} ${p(oy+poolH*0.182)} ` +
      `C ${p(ox+poolW*1.067)} ${p(oy+poolH*0.364)}, ${p(ox+poolW*1.011)} ${p(oy+poolH*0.700)}, ${p(ox+poolW*0.861)} ${p(oy+poolH*0.927)} ` +
      `C ${p(ox+poolW*0.694)} ${p(oy+poolH*1.073)}, ${p(ox+poolW*0.361)} ${p(oy+poolH*1.036)}, ${p(ox+poolW*0.156)} ${p(oy+poolH*0.882)} ` +
      `C ${p(ox-poolW*0.011)} ${p(oy+poolH*0.727)}, ${p(ox+poolW*0.011)} ${p(oy+poolH*0.309)}, ${p(ox+poolW*0.194)} ${p(oy+poolH*0.055)} Z`;
  }

  if (shape === 'grecian') {
    const clipH = poolW * 0.155;
    const clipV = poolH * 0.2;
    return `M ${p(ox+clipH)} ${oy} L ${p(rx-clipH)} ${oy} L ${rx} ${p(oy+clipV)} L ${rx} ${p(by-clipV)} L ${p(rx-clipH)} ${by} L ${p(ox+clipH)} ${by} L ${ox} ${p(by-clipV)} L ${ox} ${p(oy+clipV)} Z`;
  }

  if (shape === 'l_shape') {
    const mainW = p(poolW * 0.6);
    const wingY = p(oy + poolH * 0.427);
    return `M ${ox} ${oy} L ${p(ox+mainW)} ${oy} L ${p(ox+mainW)} ${wingY} L ${rx} ${wingY} L ${rx} ${by} L ${ox} ${by} Z`;
  }

  if (shape === 'lazy_l') {
    const mainW = p(poolW * 0.53);
    const wingY = p(oy + poolH * 0.31);
    return `M ${ox} ${oy} L ${p(ox+mainW)} ${oy} L ${p(ox+mainW)} ${wingY} L ${rx} ${wingY} L ${rx} ${by} L ${ox} ${by} Z`;
  }

  if (shape === 'kidney') {
    return `M ${p(ox+poolW*0.306)} ${p(oy+poolH*0.018)} ` +
      `C ${p(ox+poolW*0.611)} ${p(oy-poolH*0.091)}, ${p(ox+poolW*0.917)} ${p(oy+poolH*0.036)}, ${p(ox+poolW*0.989)} ${p(oy+poolH*0.382)} ` +
      `C ${p(ox+poolW*1.044)} ${p(oy+poolH*0.700)}, ${p(ox+poolW*0.889)} ${p(oy+poolH*0.973)}, ${p(ox+poolW*0.639)} ${p(oy+poolH*1.000)} ` +
      `C ${p(ox+poolW*0.389)} ${p(oy+poolH*1.018)}, ${p(ox+poolW*0.167)} ${p(oy+poolH*0.927)}, ${p(ox+poolW*0.083)} ${p(oy+poolH*0.727)} ` +
      `C ${p(ox+poolW*0.011)} ${p(oy+poolH*0.545)}, ${p(ox+poolW*0.067)} ${p(oy+poolH*0.364)}, ${p(ox+poolW*0.178)} ${p(oy+poolH*0.309)} ` +
      `C ${p(ox+poolW*0.267)} ${p(oy+poolH*0.264)}, ${p(ox+poolW*0.267)} ${p(oy+poolH*0.127)}, ${p(ox+poolW*0.306)} ${p(oy+poolH*0.018)} Z`;
  }

  // Fallback
  return `M ${ox} ${oy} L ${rx} ${oy} L ${rx} ${by} L ${ox} ${by} Z`;
}

/**
 * Generate parametric anchor data for feature positioning.
 */
export function generateShapeAnchors(shapeKey, lengthFt, widthFt) {
  const layout = computeLayout(lengthFt, widthFt);
  const { poolW, poolH, ox, oy } = layout;
  const rx = ox + poolW;
  const by = oy + poolH;
  const cx = ox + poolW / 2;
  const cy = oy + poolH / 2;
  const deckM = 18; // margin into deck for exterior features

  // Base anchors (rectangle)
  const base = {
    shallowEdge: { x1: ox, y1: oy, x2: rx, y2: oy },
    deepEdge: { x1: ox, y1: by, x2: rx, y2: by },
    leftWall: { x1: ox, y1: oy, x2: ox, y2: by },
    rightWall: { x1: rx, y1: oy, x2: rx, y2: by },
    poolCenter: { x: cx, y: cy },
    spaAttach: { left: { x: ox - deckM, y: cy }, right: { x: rx + deckM, y: cy }, center: { x: cx, y: oy - 20 } },
    slidePos: {
      left: { px: ox - deckM - 8, py: cy + poolH * 0.3, sx: ox + 8, sy: cy + poolH * 0.3 },
      right: { px: rx + deckM + 8, py: cy + poolH * 0.3, sx: rx - 8, sy: cy + poolH * 0.3 },
      deep_end: { px: cx, py: by + 15, sx: cx, sy: by - 6 },
      custom: { px: rx + deckM + 8, py: cy + poolH * 0.15, sx: rx - 8, sy: cy + poolH * 0.2 },
    },
    waterWall: { x1: ox + poolW * 0.083, y1: by, x2: rx - poolW * 0.083, y2: by },
    ledgeZone: { x: ox, y: oy, w: poolW, maxH: poolH * 0.29 },
    benchLeft: { x: ox, y: oy + poolH * 0.155, w: Math.max(poolW * 0.083, 12), h: poolH * 0.71 },
    benchDeep: { x: ox + poolW * 0.083, y: by - poolH * 0.136, w: poolW * 0.833, h: Math.max(poolH * 0.136, 12) },
    benchRight: { x: rx - poolW * 0.083, y: oy + poolH * 0.155, w: Math.max(poolW * 0.083, 12), h: poolH * 0.71 },
    coverHousing: { x: ox, y: oy, w: poolW, h: poolH * 0.10 },
    coverTrackL: { x: ox + poolW * 0.1, y1: oy, y2: by },
    coverTrackR: { x: rx - poolW * 0.1, y1: oy, y2: by },
    fireBowls: [{ x: ox - deckM, y: by + 12 }, { x: rx + deckM, y: by + 12 }],
    firePit: { x: rx + deckM, y: oy - 16 },
  };

  // Shape-specific overrides
  if (shapeKey === 'l_shape') {
    const mainW = poolW * 0.6;
    const wingY = oy + poolH * 0.427;
    return {
      ...base,
      shallowEdge: { x1: ox, y1: oy, x2: ox + mainW, y2: oy },
      rightWall: { x1: rx, y1: wingY, x2: rx, y2: by },
      ledgeZone: { x: ox, y: oy, w: mainW, maxH: poolH * 0.25 },
      benchRight: { x: rx - poolW * 0.083, y: wingY + poolH * 0.1, w: Math.max(poolW * 0.083, 12), h: (by - wingY) * 0.7 },
    };
  }

  if (shapeKey === 'lazy_l') {
    const mainW = poolW * 0.53;
    const wingY = oy + poolH * 0.31;
    return {
      ...base,
      shallowEdge: { x1: ox, y1: oy, x2: ox + mainW, y2: oy },
      rightWall: { x1: rx, y1: wingY, x2: rx, y2: by },
      ledgeZone: { x: ox, y: oy, w: mainW, maxH: poolH * 0.25 },
      benchRight: { x: rx - poolW * 0.083, y: wingY + poolH * 0.1, w: Math.max(poolW * 0.083, 12), h: (by - wingY) * 0.7 },
    };
  }

  if (shapeKey === 'roman') {
    return {
      ...base,
      waterWall: { x1: ox + poolW * 0.22, y1: by, x2: rx - poolW * 0.22, y2: by },
      ledgeZone: { x: ox + poolW * 0.22, y: oy, w: poolW * 0.56, maxH: poolH * 0.27 },
      benchLeft: { x: ox + poolW * 0.06, y: oy + poolH * 0.22, w: Math.max(poolW * 0.078, 12), h: poolH * 0.56 },
      benchRight: { x: rx - poolW * 0.06 - poolW * 0.078, y: oy + poolH * 0.22, w: Math.max(poolW * 0.078, 12), h: poolH * 0.56 },
    };
  }

  if (shapeKey === 'kidney') {
    return {
      ...base,
      shallowEdge: { x1: ox + poolW * 0.22, y1: oy, x2: rx - poolW * 0.14, y2: oy },
      ledgeZone: { x: ox + poolW * 0.28, y: oy, w: poolW * 0.56, maxH: poolH * 0.25 },
      benchLeft: { x: ox + poolW * 0.18, y: oy + poolH * 0.18, w: Math.max(poolW * 0.072, 12), h: poolH * 0.5 },
      benchRight: { x: rx - poolW * 0.14 - poolW * 0.072, y: oy + poolH * 0.2, w: Math.max(poolW * 0.072, 12), h: poolH * 0.5 },
    };
  }

  if (shapeKey === 'compact_plunge') {
    return { ...base, coverHousing: { x: ox, y: oy, w: poolW, h: poolH * 0.10 } };
  }

  // auto_cover_rectangle, negative_edge, geometric_custom, freeform, grecian → use base
  return base;
}


// Clickable placement zones per feature type
// Each zone has: id (maps to stored value), label, rect {x,y,w,h} for hit area + highlight
export function getPlacementZones(shapeKey, featureType) {
  const sd = getShapeData(shapeKey);
  if (!sd) return [];

  const se = sd.shallowEdge;
  const lw = sd.leftWall;
  const rw = sd.rightWall;
  const de = sd.deepEdge;

  if (featureType === 'entry_type') {
    return [
      { id: 'corner_steps', label: 'Corner', rect: { x: se.x1, y: se.y1 - 4, w: 42, h: 32 } },
      { id: 'full_width_steps', label: 'Full Width', rect: { x: se.x1 + 2, y: se.y1 - 4, w: se.x2 - se.x1 - 4, h: 28 } },
      { id: 'side_steps', label: 'Side', rect: { x: lw.x1 - 12, y: lw.y1 + 24, w: 18, h: 50 } },
      { id: 'recessed_steps', label: 'Recessed', rect: { x: (se.x1 + se.x2) / 2 - 32, y: se.y1 - 14, w: 64, h: 22 } },
    ];
  }

  if (featureType === 'tanning_ledge_size') {
    const lz = sd.ledgeZone;
    return [
      { id: 'small', label: 'Compact', rect: { x: lz.x + lz.w * 0.62, y: lz.y, w: lz.w * 0.36, h: lz.maxH * 0.55 } },
      { id: 'medium', label: 'Standard', rect: { x: lz.x + lz.w * 0.2, y: lz.y, w: lz.w * 0.62, h: lz.maxH * 0.78 } },
      { id: 'large', label: 'Extended', rect: { x: lz.x + lz.w * 0.06, y: lz.y, w: lz.w * 0.88, h: lz.maxH } },
    ];
  }

  if (featureType === 'bench_seating') {
    return [
      { id: 'one_side', label: 'Left Wall', rect: { x: sd.benchLeft.x - 2, y: sd.benchLeft.y, w: sd.benchLeft.w + 4, h: sd.benchLeft.h } },
      { id: 'deep_end', label: 'Deep End', rect: { x: sd.benchDeep.x, y: sd.benchDeep.y - 2, w: sd.benchDeep.w, h: sd.benchDeep.h + 4 } },
      { id: 'wraparound', label: 'Wrap', rect: { x: sd.benchLeft.x - 2, y: sd.benchLeft.y, w: (sd.benchRight?.x || rw.x1) - sd.benchLeft.x + (sd.benchRight?.w || 15) + 4, h: (sd.benchDeep?.y || de.y1) - sd.benchLeft.y + (sd.benchDeep?.h || 15) + 4 } },
    ];
  }

  if (featureType === 'spa_location') {
    return [
      { id: 'left', label: 'Left', rect: { x: sd.spaAttach.left.x - 25, y: sd.spaAttach.left.y - 25, w: 50, h: 50 } },
      { id: 'right', label: 'Right', rect: { x: sd.spaAttach.right.x - 25, y: sd.spaAttach.right.y - 25, w: 50, h: 50 } },
      { id: 'center', label: 'Center', rect: { x: sd.spaAttach.center.x - 25, y: sd.spaAttach.center.y - 25, w: 50, h: 50 } },
    ];
  }

  return [];
}
